import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, MoreHorizontal, Plus, X } from 'lucide-react';
import { describeTree } from './AutomationScenarios';

const statuses = { draft: 'Draft', active: 'Active', paused: 'Paused', stopped: 'Stopped' };
const explanations = {
  draft: 'Not started. Complete the settings before starting the chain.',
  active: 'Accepts new players and processes existing participants.',
  paused: 'No new players enter. Existing participants continue to the end.',
  stopped: 'No new players enter. Current participation is ended and no further messages are sent.'
};
const date = (value) => value ? `${new Date(value).toLocaleString('en-GB', {
  timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
})} UTC` : '—';
const channelNames = (chain) => chain.channels.map((channel) => channel === 'sms' ? 'SMS' : 'Email').join(' / ') || '—';

async function request(url, method = 'GET', body) {
  const response = await fetch(url, { method, headers: {
    Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
  }, body: body ? JSON.stringify(body) : undefined });
  if (!(response.headers.get('content-type') || '').includes('application/json')) throw new Error('The server could not complete this request. Reload and try again.');
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.errors?.join('. ') || 'Could not complete the request. Try again.');
    error.issues = data.issues;
    throw error;
  }
  return data;
}

function Dialog({ title, onClose, busy, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    ref.current.showModal();
    ref.current.querySelector('input[autofocus], input')?.focus();
    return () => previous?.focus();
  }, []);
  return <dialog ref={ref} className='automation-chains-dialog' aria-labelledby='chain-dialog-title'
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}>
    <header><h2 id='chain-dialog-title'>{title}</h2>
      <button type='button' className='chain-button chain-button--ghost' aria-label='Close' disabled={busy} onClick={onClose}>
        <X size={16} strokeWidth={1.6} />
      </button>
    </header>
    {children}
  </dialog>;
}

export default function AutomationChains({ initialChains = [], total: initialTotal = 0, page = 1, archived = false,
  events = [], segments = [], attributes = [], smsTemplates = [], emailTemplates = [], emailSenders = [] }) {
  const [chains, setChains] = useState(initialChains);
  const [total, setTotal] = useState(initialTotal);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activationIssues, setActivationIssues] = useState([]);
  const [newName, setNewName] = useState('');
  const listUrl = `/automation-chains?page=${page}&archived=${archived}`;
  const byAttribute = Object.fromEntries(attributes.map((attribute) => [attribute.id, attribute]));
  const trigger = (chain) => (chain.display_trigger_kind || chain.trigger_kind) === 'conditions'
    ? describeTree(chain.display_trigger_conditions || chain.trigger_conditions, byAttribute, segments) : chain.trigger_name || '—';
  const close = () => { setModal(null); setError(''); setActivationIssues([]); };

  useEffect(() => {
    const dismiss = (event) => document.querySelectorAll('.chain-action-menu[open]').forEach((menu) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !menu.contains(event.target)) menu.open = false;
    });
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, []);

  const refresh = async () => {
    const data = await request(listUrl);
    setChains(data.initialChains); setTotal(data.total);
  };
  const perform = async (chain, action) => {
    if (busy) return;
    setBusy(true); setError(''); setActivationIssues([]);
    try {
      await request(`/automation-chains/${chain.id}${action === 'delete' ? '' : `/${action}`}`, action === 'delete' ? 'DELETE' : 'POST');
      if (action === 'duplicate') { window.location.assign('/automation-chains'); return; }
      setModal(null);
      await refresh();
    } catch (err) { setError(err.message); setActivationIssues(err.issues || []); }
    finally { setBusy(false); }
  };
  const open = (chain) => window.location.assign(`/automation-chains/${chain.id}/builder`);
  const create = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const chain = await request('/automation-chains', 'POST', {automation_chain:{name:newName}});
      open(chain);
    } catch (err) { setError(err.message); setBusy(false); }
  };
  const confirm = (chain, mode) => { setError(''); setActivationIssues([]); setModal({ mode, chain }); };
  const current = modal?.chain;
  const confirmText = current && {
    activate: `Start “${current.name}”? The server will check all node settings and connections before allowing players to enter.`,
    stop: `Stop “${current.name}”? All current participants will exit. Waiting and queued messages will be cancelled. Messages already submitted to a provider cannot be recalled.`,
    archive: `Archive “${current.name}”? The chain will stop and current participants will exit. Its settings and history will be preserved.`,
    delete: `Delete draft “${current.name}”? This cannot be undone.`
  }[modal.mode];

  return <main className='automation-chains'>
    <header className='chain-page-header'>
      <div><h1>Automation chains</h1><p>Create and manage automated SMS and Email communications.</p></div>
      <div className='chain-header-actions'>
      <a className='chain-button' href='/docs/release-6'><BookOpen size={16} strokeWidth={1.6} />Documentation</a>
      <button className='chain-button chain-button--primary' disabled={busy} onClick={() => { setError(''); setModal({ mode: 'new' }); }}>
        <Plus size={16} strokeWidth={1.6} />New chain
      </button>
      </div>
    </header>
    <nav className='chain-tabs' aria-label='Chain lists'>
      <a href='/automation-chains' aria-current={!archived ? 'page' : undefined}>Chains</a>
      <a href='/automation-chains?archived=true' aria-current={archived ? 'page' : undefined}>Archive</a>
    </nav>
    {error && !modal && <div role='alert' className='chain-error'>{error}
      <button className='chain-button' disabled={busy} onClick={async () => {
        try { await refresh(); setError(''); } catch (err) { setError(err.message); }
      }}>Refresh list</button>
    </div>}
    <div className='chain-table-wrap' aria-busy={busy}>
      <table className='chain-table'>
        <thead><tr>{['Name', 'Trigger', 'Channels', 'Status', 'Created', 'Last updated', 'Actions'].map((label) =>
          <th key={label} scope='col'>{label}</th>)}</tr></thead>
        <tbody>{chains.map((chain) => <tr key={chain.id}>
          <td><button disabled={busy} className='chain-link' onClick={() => open(chain, 'show')}>{chain.name}</button></td>
          <td>{trigger(chain)}</td><td>{channelNames(chain)}</td>
          <td><span className={`chain-status chain-status--${chain.status}`} title={explanations[chain.status]}>{statuses[chain.status]}</span></td>
          <td className='chain-date'>{date(chain.created_at)}</td><td className='chain-date'>{date(chain.updated_at)}</td>
          <td><div className='chain-actions'>
            {!archived && chain.status !== 'active' && <button className='chain-button chain-button--ghost' disabled={busy}
              onClick={() => confirm(chain, 'activate')}>{chain.status === 'paused' ? 'Resume' : 'Start'}</button>}
            {!archived && chain.status === 'active' && <button className='chain-button chain-button--ghost' disabled={busy}
              onClick={() => perform(chain, 'pause')}>Pause</button>}
            <details className='chain-action-menu' onToggle={(event) => {
              if (!event.currentTarget.open) return;
              const menu = event.currentTarget.querySelector('.chain-menu-items');
              const bounds = event.currentTarget.querySelector('summary').getBoundingClientRect();
              menu.style.top = `${Math.max(8, Math.min(bounds.bottom, window.innerHeight - menu.offsetHeight - 8))}px`;
              menu.style.left = `${Math.max(8, bounds.right - 170)}px`;
            }}>
              <summary aria-label={`Actions for ${chain.name}`}><MoreHorizontal size={18} strokeWidth={1.6} /></summary>
              <div className='chain-menu-items' onClick={(event) => {
                if (event.target.closest('button')) event.currentTarget.closest('details').open = false;
              }}>
                <button disabled={busy} onClick={(event) => { event.currentTarget.closest('details').open = false; open(chain, 'show'); }}>Open</button>
                <a href={`/automation-chains/${chain.id}/results`}>Results</a>
                {!archived && <button disabled={busy} onClick={(event) => { event.currentTarget.closest('details').open = false; open(chain, 'edit'); }}>Edit</button>}
                <button disabled={busy} onClick={() => perform(chain, 'duplicate')}>Create copy</button>
                {!archived && <>
                  {['active', 'paused'].includes(chain.status) && <button disabled={busy} className='chain-danger' onClick={() => confirm(chain, 'stop')}>Stop</button>}
                  <button disabled={busy} className='chain-danger' onClick={() => confirm(chain, 'archive')}>Archive</button>
                  {chain.status === 'draft' && <button disabled={busy} className='chain-danger' onClick={() => confirm(chain, 'delete')}>Delete</button>}
                </>}
              </div>
            </details>
          </div></td>
        </tr>)}</tbody>
      </table>
      {!chains.length && <div className='chain-empty'>
        <p>{archived ? 'No archived chains.' : page > 1 ? 'No chains on this page.' : 'No automation chains yet.'}</p>
        {!archived && page === 1 && <button className='chain-button' onClick={() => setModal({ mode: 'new' })}>Create a chain</button>}
      </div>}
    </div>
    <footer className='chain-pagination'><span>{total.toLocaleString('en-US')} chains</span>
      <div>{page > 1 && <a className='chain-button' href={`/automation-chains?page=${page - 1}&archived=${archived}`}>Previous</a>}
        <span>Page {page.toLocaleString('en-US')}</span>
        {page * 50 < total && <a className='chain-button' href={`/automation-chains?page=${page + 1}&archived=${archived}`}>Next</a>}
      </div>
    </footer>
    <p className='chain-help'>Paused: existing participants continue; new entry is closed. Stopped: participation ends and no further messages are sent.</p>
    {modal?.mode === 'new' && <Dialog title='New automation chain' busy={busy} onClose={close}>
      <form onSubmit={create}>
        <label className='chain-create-label'>Name<input autoFocus required value={newName} onChange={(event) => setNewName(event.target.value)} /></label>
        <p className='chain-help'>Create a draft, then add and connect steps in the visual builder.</p>
        {error && <p role='alert' className='chain-error'>{error}</p>}
        <footer><button type='button' className='chain-button' disabled={busy} onClick={close}>Cancel</button>
          <button type='submit' className='chain-button chain-button--primary' disabled={busy}>{busy ? 'Creating…' : 'Create chain'}</button></footer>
      </form>
    </Dialog>}
    {confirmText && <Dialog title={modal.mode === 'activate' ? 'Review before activation' : `${modal.mode[0].toUpperCase()}${modal.mode.slice(1)} chain`} busy={busy} onClose={close}>
      <p>{confirmText}</p>
      {modal.mode === 'activate' && <p><a className='chain-link' href={`/automation-chains/${current.id}/builder`}>Review diagram and node settings</a></p>}
      {error && (activationIssues.length ? <div role='alert' className='chain-error'><p>Cannot start this chain. Fix the following issues:</p>
        <ul>{activationIssues.map((issue,index) => <li key={index}>{issue.node_id
          ? <a className='chain-link' href={`/automation-chains/${current.id}/builder?node=${encodeURIComponent(issue.node_id)}`}>{issue.node_name || issue.node_id}: {issue.message}</a>
          : issue.message}</li>)}</ul></div> : <p role='alert' className='chain-error'>{error}</p>)}
      <footer><button className='chain-button' disabled={busy} onClick={close}>Cancel</button>
        <button className={`chain-button ${modal.mode === 'activate' ? 'chain-button--primary' : 'chain-danger'}`} disabled={busy} onClick={() => perform(current, modal.mode)}>
          {busy ? 'Working…' : modal.mode === 'activate' ? current.status === 'paused' ? 'Resume chain' : 'Start chain' : `${modal.mode[0].toUpperCase()}${modal.mode.slice(1)} chain`}
        </button></footer>
    </Dialog>}
  </main>;
}
